import { useWorkflowBlockEdit } from "@/pages/workflows/edit/flow/use-workflow-block-edit.tsx";
import { useWorkflows } from "@/pages/workflows/workflow-hook.tsx";
import { WorkflowAddBlockMenuSub } from "@/pages/workflows/edit/flow/WorkflowAddBlockMenuItems.tsx";
import {
  useWorkflowDeleteBlock,
  useWorkflowDuplicateBlock,
  useWorkflowMoveBlock,
  workflowBlockMoveMutationKey,
} from "@/queries/workflows/workflow.query.ts";
import { getApiNotificationChannelTypeTitle } from "@/types/notification.types.ts";
import { WorkflowBlockNode } from "@/types/workflows/workflow-tree-builder.ts";
import {
  ApiWorkflowBlockActionCandidateStageChangeMode,
  getApiWorkflowBlockActionNotificationRecipientTypesTitle,
  getEsignSignatureTypeTitle,
  getWorkflowApprovalWriteResponsesTypeTitle,
} from "@/types/workflows/workflow.action-notification.types.ts";
import {
  ApiWorkflowBlockActionTypes,
  ApiWorkflowBlockTypes,
} from "@/types/workflows/workflow.base-enum.types.ts";
import {
  ApiWorkflowBlock,
  ApiWorkflowBlockEdge,
  ApiWorkflowVersion,
} from "@/types/workflows/workflow.types.ts";
import {
  Avatar,
  Box,
  ConfirmationModal,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useIsMutating } from "@tanstack/react-query";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";

interface WorkflowBlockProps {
  node: WorkflowBlockNode;
  workflowVersion: ApiWorkflowVersion;
  isLast: boolean;
  readOnly?: boolean;
  continuationLineLevels: number[];
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  showDragHandle?: boolean;
  isDragDisabled?: boolean;
  isDragging?: boolean;
  decorativeLevel?: number;
  onDragHandleStart?: (clientX: number) => void;
}

const getDescendantActionsCount = (node: WorkflowBlockNode): number =>
  node.children.reduce(
    (count, child) =>
      count +
      (child.block.block_type === ApiWorkflowBlockTypes.action ? 1 : 0) +
      getDescendantActionsCount(child),
    0
  );

export const WorkflowBlock = ({
  node,
  workflowVersion,
  isLast,
  readOnly = false,
  continuationLineLevels,
  dragHandleProps,
  showDragHandle = false,
  isDragDisabled = false,
  isDragging = false,
  decorativeLevel,
  onDragHandleStart,
}: WorkflowBlockProps) => {
  const { block } = node;
  const visualLevel = Math.max(0, node.level - 1);
  const treeLineLevel = decorativeLevel ?? visualLevel;
  const indentSize = 24;
  const { t } = useTranslation();

  const { handleWorkflowBlockEdit } = useWorkflowBlockEdit();
  const { getWorkflowBlockTimingDescription } = useWorkflows();
  const { openModal } = useModals();
  const deleteBlock = useWorkflowDeleteBlock();
  const duplicateBlock = useWorkflowDuplicateBlock();
  const moveBlock = useWorkflowMoveBlock();
  const isMovePending = useIsMutating({ mutationKey: workflowBlockMoveMutationKey }) > 0;

  if (block.block_type != ApiWorkflowBlockTypes.action) return null;

  const incomingEdge = node.incomingEdges.length === 1 ? node.incomingEdges[0] : undefined;
  const parentBlockId = incomingEdge?.from_block_id;
  const sortEdges = (left: ApiWorkflowBlockEdge, right: ApiWorkflowBlockEdge) =>
    left.priority - right.priority || left.id - right.id;
  const siblingEdges =
    parentBlockId === undefined
      ? []
      : workflowVersion.edges
          .filter((edge) => edge.from_block_id === parentBlockId)
          .sort(sortEdges);
  const siblingIndex = incomingEdge
    ? siblingEdges.findIndex((edge) => edge.id === incomingEdge.id)
    : -1;
  const previousSiblingEdge = siblingIndex > 0 ? siblingEdges[siblingIndex - 1] : undefined;
  const previousSibling = workflowVersion.blocks.find(
    (item) => item.id === previousSiblingEdge?.to_block_id
  );
  const parentBlock = workflowVersion.blocks.find((item) => item.id === parentBlockId);
  const parentIncomingEdges = parentBlock
    ? workflowVersion.edges.filter((edge) => edge.to_block_id === parentBlock.id)
    : [];
  const parentIncomingEdge = parentIncomingEdges.length === 1 ? parentIncomingEdges[0] : undefined;
  const grandparentBlockId = parentIncomingEdge?.from_block_id;
  const grandparentSiblingEdges =
    grandparentBlockId === undefined
      ? []
      : workflowVersion.edges
          .filter((edge) => edge.from_block_id === grandparentBlockId)
          .sort(sortEdges);
  const parentIndex = parentIncomingEdge
    ? grandparentSiblingEdges.findIndex((edge) => edge.id === parentIncomingEdge.id)
    : -1;

  const canMoveUp = incomingEdge !== undefined && siblingIndex > 0;
  const canMoveDown =
    incomingEdge !== undefined && siblingIndex >= 0 && siblingIndex < siblingEdges.length - 1;
  const canIndent =
    incomingEdge !== undefined &&
    previousSibling?.block_type === ApiWorkflowBlockTypes.action &&
    previousSibling.id !== block.id;
  const canOutdent =
    incomingEdge !== undefined &&
    parentBlock?.block_type === ApiWorkflowBlockTypes.action &&
    grandparentBlockId !== undefined &&
    parentIndex >= 0;
  const isRootLevelBlock = parentBlock?.block_type === ApiWorkflowBlockTypes.start;

  const isMoveUpDisabled = !canMoveUp || isMovePending;
  const isMoveDownDisabled = !canMoveDown || isMovePending;
  const isIndentDisabled = !canIndent || isMovePending;

  const handleMove = (newParentBlockId: number | undefined, position: number | undefined) => {
    if (isMovePending || newParentBlockId === undefined || position === undefined) return;
    if (newParentBlockId === parentBlockId && position === siblingIndex) return;

    moveBlock.mutate({
      workflowId: workflowVersion.workflow_id,
      workflowVersionId: workflowVersion.id,
      workflowBlockId: block.id,
      payload: {
        parent_block_id: newParentBlockId,
        position,
      },
    });
  };

  const descendantActionsCount = getDescendantActionsCount(node);
  const isBlockingAction =
    descendantActionsCount > 0 &&
    (block.action_type === ApiWorkflowBlockActionTypes.task ||
      block.action_type === ApiWorkflowBlockActionTypes.run_workflow ||
      block.action_type === ApiWorkflowBlockActionTypes.survey);

  const getIcon = (blockActionType: ApiWorkflowBlockActionTypes) => {
    if (blockActionType == ApiWorkflowBlockActionTypes.send_notification) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-paper-plane"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.http_request) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-globe"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.google_workspace) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-brands fa-google"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.invitation) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-envelope-open-text"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.survey) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-square-poll-horizontal"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.task) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-check"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.performance_review) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-sharp fa-light fa-comment-pen"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.performance_review_enroll) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-user-plus"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.survey_enroll) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-user-plus"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.run_workflow) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-diagram-project"} />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.survey_with_approval) {
      return <Avatar icon={<FontAwesomeIcon icon="fa-light fa-check-double" />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.esign) {
      return <Avatar icon={<FontAwesomeIcon icon="fa-light fa-signature" />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.meeting) {
      return <Avatar icon={<FontAwesomeIcon icon="fa-light fa-calendar" />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.candidate_email) {
      return <Avatar icon={<FontAwesomeIcon icon="fa-light fa-envelope" />} />;
    } else if (blockActionType == ApiWorkflowBlockActionTypes.candidate_stage_change) {
      return <Avatar icon={<FontAwesomeIcon icon="fa-light fa-arrow-right-arrow-left" />} />;
    }
    return null;
  };

  const getPreTitle = (block: ApiWorkflowBlock) => {
    if (block.title) return block.title;
    switch (block.action_type) {
      case ApiWorkflowBlockActionTypes.send_notification:
        return t("workflow:action_type_notification");
      case ApiWorkflowBlockActionTypes.http_request:
        return t("workflow:action_type_http_request");
      case ApiWorkflowBlockActionTypes.google_workspace:
        return t("workflow:action_type_google_workspace");
      case ApiWorkflowBlockActionTypes.invitation:
        return t("workflow:action_type_invitation");
      case ApiWorkflowBlockActionTypes.survey:
        return t("workflow:action_type_survey");
      case ApiWorkflowBlockActionTypes.task:
        return t("workflow:blocking_action_type_task");
      case ApiWorkflowBlockActionTypes.performance_review:
        return t("performance_review:create_performance_review");
      case ApiWorkflowBlockActionTypes.performance_review_enroll:
        return t("workflow:action_type_performance_review_add_employee");
      case ApiWorkflowBlockActionTypes.survey_enroll:
        return t("workflow:action_type_survey_enroll");
      case ApiWorkflowBlockActionTypes.run_workflow:
        return t("workflow:blocking_action_type_run_workflow");
      case ApiWorkflowBlockActionTypes.survey_with_approval:
        return t("workflow:action_type_approval");
      case ApiWorkflowBlockActionTypes.esign:
        return t("workflow:action_type_esign");
      case ApiWorkflowBlockActionTypes.meeting:
        return t("workflow:action_type_meeting");
      case ApiWorkflowBlockActionTypes.candidate_email:
        return t("workflow:action_type_candidate_email");
      case ApiWorkflowBlockActionTypes.candidate_stage_change:
        return t("workflow:action_type_candidate_stage_change");
    }
    return undefined;
  };

  const getTitleSlot = (block: ApiWorkflowBlock) => {
    switch (block.action_type) {
      case ApiWorkflowBlockActionTypes.send_notification:
        if (block.action_notification) {
          return <Text className={"text-default"}>{block.action_notification.subject || "-"}</Text>;
        }
        break;
      case ApiWorkflowBlockActionTypes.http_request:
        if (block.action_http_request) {
          return <Text className={"text-default"}>{block.action_http_request.url || "-"}</Text>;
        }
        break;
      case ApiWorkflowBlockActionTypes.google_workspace:
        if (block.action_google_workspace) {
          return (
            <Text className={"text-default"}>
              {t(
                `workflow:action_type_google_workspace_${block.action_google_workspace.action_type}`
              ) || "-"}
            </Text>
          );
        }
        break;
      case ApiWorkflowBlockActionTypes.invitation:
        if (block.action_invitation) {
          return <Text className={"text-default"}>{block.action_invitation.subject || "-"}</Text>;
        }
        break;
      case ApiWorkflowBlockActionTypes.survey:
        if (block.action_survey) {
          return <Text className={"text-default"}>{block.action_survey.title || "-"}</Text>;
        }
        break;
      case ApiWorkflowBlockActionTypes.task:
        if (block.action_task) {
          return <Text className={"text-default"}>{block.action_task.title || "-"}</Text>;
        }
        break;
      case ApiWorkflowBlockActionTypes.performance_review:
        if (block.action_performance_review) {
          return (
            <Text className={"text-default"}>{block.action_performance_review.title || "-"}</Text>
          );
        }
        break;
      case ApiWorkflowBlockActionTypes.performance_review_enroll:
        return (
          <Text className={"text-default"}>
            {t("workflow:action_type_performance_review_add_employee_description")}
          </Text>
        );
      case ApiWorkflowBlockActionTypes.survey_enroll:
        return (
          <Text className={"text-default"}>
            {t("workflow:action_type_survey_enroll_description")}
          </Text>
        );
      case ApiWorkflowBlockActionTypes.run_workflow:
        return (
          <Text className={"text-default"}>
            {block.action_run_workflow?.target_workflow?.title || "-"}
          </Text>
        );
      case ApiWorkflowBlockActionTypes.survey_with_approval:
        return (
          <Text className={"text-default"}>
            {block.action_survey_with_approval
              ? getWorkflowApprovalWriteResponsesTypeTitle(
                  block.action_survey_with_approval.when_to_save_responses
                )
              : "-"}
          </Text>
        );
      case ApiWorkflowBlockActionTypes.esign:
        return (
          <Text className={"text-default"}>
            {block.action_esign
              ? getEsignSignatureTypeTitle(block.action_esign.signature_type)
              : "-"}
          </Text>
        );
      case ApiWorkflowBlockActionTypes.meeting:
        return <Text className={"text-default"}>{block.action_meeting?.title || "-"}</Text>;
      case ApiWorkflowBlockActionTypes.candidate_email:
        return (
          <Text className={"text-default"}>{block.action_candidate_email?.subject || "-"}</Text>
        );
      case ApiWorkflowBlockActionTypes.candidate_stage_change:
        return (
          <Text className={"text-default"}>
            {block.action_candidate_stage_change
              ? t(
                  block.action_candidate_stage_change.mode ===
                    ApiWorkflowBlockActionCandidateStageChangeMode.disqualify
                    ? "workflow:action_type_candidate_stage_change_mode_disqualify"
                    : "workflow:action_type_candidate_stage_change_mode_change_stage"
                )
              : "-"}
          </Text>
        );
    }
    return <Text className={"text-default"}>-</Text>;
  };

  const getCaptionSlot = (block: ApiWorkflowBlock) => {
    switch (block.action_type) {
      case ApiWorkflowBlockActionTypes.send_notification:
        if (block.action_notification) {
          return (
            <DotSeparator>
              <Text className={"text-secondary"}>
                {getApiWorkflowBlockActionNotificationRecipientTypesTitle(
                  block.action_notification.recipient_type
                )}
              </Text>
              {block.action_notification.recipient_entities.map((entity) => (
                <Text className={"text-secondary"} key={entity.title}>
                  {entity.title}
                </Text>
              ))}
              {block.action_notification.to_channels.map((entity) => (
                <Text className={"text-secondary"} key={entity}>
                  {getApiNotificationChannelTypeTitle(entity)}
                </Text>
              ))}
            </DotSeparator>
          );
        }
    }

    return undefined;
  };

  const getBlockCaptionSlot = (block: ApiWorkflowBlock) => {
    const timingDescription = getWorkflowBlockTimingDescription(
      block,
      workflowVersion.trigger_schedule_rule,
      isRootLevelBlock
    );

    return (
      <DotSeparator>
        {timingDescription && <Text className="text-secondary">{timingDescription}</Text>}
        {getCaptionSlot(block)}
      </DotSeparator>
    );
  };

  const getValueSlot = (block: ApiWorkflowBlock) => {
    if (readOnly) {
      const readOnlyConditionsCount =
        node.incomingEdges?.reduce((acc, edge) => acc + edge.conditions.length, 0) +
        (Math.abs(block.delay_days) > 0 ||
        Math.abs(block.delay_hours) > 0 ||
        Math.abs(block.delay_minutes) > 0
          ? 1
          : 0);
      if (readOnlyConditionsCount > 0) {
        return (
          <Text className={"text-default"}>
            {t("plurals:conditions", { count: readOnlyConditionsCount })}
          </Text>
        );
      }
      return undefined;
    }

    const actions = (
      <>
        <WorkflowAddBlockMenuSub
          workflowId={workflowVersion.workflow_id}
          workflowVersion={workflowVersion}
          fromBlockId={block.id}
        />
        <DropdownMenuItem
          title={t("workflow:move_block_up")}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up" />}
          disabled={isMoveUpDisabled}
          className={isMoveUpDisabled ? "opacity-50 cursor-default" : undefined}
          onClick={() => handleMove(parentBlockId, siblingIndex - 1)}
        />
        <DropdownMenuItem
          title={t("workflow:move_block_down")}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-down" />}
          disabled={isMoveDownDisabled}
          className={isMoveDownDisabled ? "opacity-50 cursor-default" : undefined}
          onClick={() => handleMove(parentBlockId, siblingIndex + 1)}
        />
        <DropdownMenuItem
          title={t("workflow:indent_block")}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-indent" />}
          disabled={isIndentDisabled}
          className={isIndentDisabled ? "opacity-50 cursor-default" : undefined}
          onClick={() => {
            if (!previousSibling) return;
            const position = workflowVersion.edges.filter(
              (edge) => edge.from_block_id === previousSibling.id
            ).length;
            handleMove(previousSibling.id, position);
          }}
        />
        {canOutdent && (
          <DropdownMenuItem
            title={t("workflow:outdent_block")}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-outdent" />}
            disabled={isMovePending}
            className={isMovePending ? "opacity-50 cursor-default" : undefined}
            onClick={() => handleMove(grandparentBlockId, parentIndex + 1)}
          />
        )}
        <DropdownMenuItem
          title={t("common:edit")}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
          onClick={() => {
            handleWorkflowBlockEdit(block, workflowVersion);
          }}
        />
        <DropdownMenuItem
          title={t("common:duplicate")}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-copy"} />}
          disabled={duplicateBlock.isPending}
          className={duplicateBlock.isPending ? "opacity-50 cursor-default" : undefined}
          onClick={() => {
            duplicateBlock.mutate({
              workflowId: workflowVersion.workflow_id,
              workflowVersionId: workflowVersion.id,
              workflowBlockId: block.id,
            });
          }}
        />
        <DropdownMenuItem
          title={t("common:delete") + "..."}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          onClick={() => {
            openModal(ConfirmationModal, {
              question: t("common:attention"),
              text: t("common:delete_confirm"),
              acceptTitle: t("common:delete"),
              onAccept: async function () {
                try {
                  await deleteBlock.mutateAsync({
                    workflowId: workflowVersion.workflow_id,
                    workflowVersionId: workflowVersion.id,
                    workflowBlockId: block.id,
                  });
                } catch {
                  return;
                }
              },
            });
          }}
        />
      </>
    );

    const conditionsCount =
      node.incomingEdges?.reduce((acc, edge) => acc + edge.conditions.length, 0) +
      (Math.abs(block.delay_days) > 0 ||
      Math.abs(block.delay_hours) > 0 ||
      Math.abs(block.delay_minutes) > 0
        ? 1
        : 0);
    if (conditionsCount > 0) {
      return (
        <Stack direction={"horizontal"} gap={"sm"} items={"center"} justify={"end"}>
          <Text className={"text-default whitespace-nowrap"}>
            {t("plurals:conditions", { count: conditionsCount })}
          </Text>
          <DropdownMenu>{actions}</DropdownMenu>
        </Stack>
      );
    }

    return <DropdownMenu>{actions}</DropdownMenu>;
  };

  return (
    <div
      className={`relative ${isDragging ? "opacity-80" : ""}`}
      style={{ marginLeft: `${visualLevel * indentSize}px` }}
    >
      {continuationLineLevels.map((lineLevel) => (
        <div
          key={`workflow-continuation-line-${lineLevel}`}
          className={"absolute w-px bg-dark/10"}
          style={{
            left: `${(lineLevel - treeLineLevel) * indentSize - Math.floor(indentSize / 2)}px`,
            top: "-8px",
            height: "calc(100% + 8px)",
          }}
        />
      ))}
      {treeLineLevel > 0 && (
        <>
          <div
            className={"absolute w-px bg-dark/10"}
            style={{
              left: `${-Math.floor(indentSize / 2)}px`,
              top: "-8px",
              height: isLast ? "calc(50% + 8px)" : "calc(100% + 8px)",
            }}
          />
          <div
            className={"absolute h-px bg-dark/10"}
            style={{
              left: `${-Math.floor(indentSize / 2)}px`,
              top: "50%",
              width: `${Math.floor(indentSize / 2)}px`,
            }}
          />
        </>
      )}
      <Box variant={"whiteBorder"} className={"py-4"}>
        <div className="flex items-center">
          {showDragHandle && (
            <div
              className={`w-8 shrink-0 self-stretch pt-2.5 text-secondary ${
                isDragDisabled
                  ? "cursor-not-allowed opacity-40"
                  : "cursor-grab active:cursor-grabbing"
              }`}
              {...(isDragDisabled ? undefined : dragHandleProps)}
              aria-label={t("common:drag_to_reorder")}
              aria-disabled={isDragDisabled}
              onMouseDownCapture={
                isDragDisabled ? undefined : (event) => onDragHandleStart?.(event.clientX)
              }
              onTouchStartCapture={
                isDragDisabled
                  ? undefined
                  : (event) => onDragHandleStart?.(event.touches[0]?.clientX ?? 0)
              }
            >
              <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <ListItem
              icon={getIcon(block.action_type)}
              behavior={"card"}
              preTitleSlot={
                <Text className={"text-secondary"}>
                  <DotSeparator>
                    <Text>{getPreTitle(block)}</Text>
                    {isBlockingAction && (
                      <Tooltip>
                        <TooltipTrigger>
                          <Text className={"text-warning cursor-help"}>
                            {t("workflow:blocking_action_count", {
                              count: descendantActionsCount,
                            })}
                          </Text>
                        </TooltipTrigger>
                        <TooltipContent>{t("workflow:blocking_action_description")}</TooltipContent>
                      </Tooltip>
                    )}
                  </DotSeparator>
                </Text>
              }
              titleSlot={<Text className={"font-semibold"}>{getTitleSlot(block)}</Text>}
              captionSlot={getBlockCaptionSlot(block)}
              valueSlot={getValueSlot(block)}
            />
          </div>
        </div>
      </Box>
    </div>
  );
};
